import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./i18n";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
// react-router-dom
import { BrowserRouter } from "react-router-dom";

// axios
import axios from "axios";

// react-redux
import { Provider } from "react-redux";

// Provider
import store from "./store/Provider";
import { OPEN_LOADER, CLOSE_LOADER } from "./store/Loader/loader.type";

// BaseUrl
import { baseURL } from "./util/config";
import { notifyPermissionDenied } from "./util/ModuleAccessContext";
import {
  CLIENT_HEADER,
  clientKind,
  sessionStorageKey,
  canRefresh,
  ensureFreshSession,
  forceLogin,
  getSession,
  isApiUrl,
  isSessionError,
  isSignedIn,
  purgeLegacyTokens,
  subscribeSession,
} from "./util/authSession";
import { SET_ADMIN, SESSION_ENDED } from "./store/Admin/admin.type";


axios.defaults.baseURL = baseURL;

// Tokens from the previous (localStorage) build must not linger.
purgeLegacyTokens();

// Keep redux in step with the stored session: refreshes in this tab, and
// sign-in / sign-out in another tab (the `storage` event).
subscribeSession((session) => {
  if (session) store.dispatch({ type: SET_ADMIN, payload: session });
  else if (store.getState()?.admin?.isAuth) store.dispatch({ type: SESSION_ENDED });
});
window.addEventListener("storage", (event) => {
  if (event.key !== sessionStorageKey() && event.key !== null) return;
  const session = getSession();
  if (session) store.dispatch({ type: SET_ADMIN, payload: session });
  else if (store.getState()?.admin?.isAuth) store.dispatch({ type: SESSION_ENDED });
});

const setHeader = (req, name, value) => {
  req.headers = req.headers || {};
  if (typeof req.headers.set === "function") req.headers.set(name, value);
  else req.headers[name] = value;
};
const removeHeader = (req, name) => {
  if (!req.headers) return;
  if (typeof req.headers.delete === "function") req.headers.delete(name);
  else delete req.headers[name];
};

// A request to the API (not S3 / Mux / other hosts) — relative URLs resolve
// against axios.defaults.baseURL.
const isApiRequest = (req) => {
  const url = req?.url || "";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url) || url.startsWith("//")) return isApiUrl(url);
  return isApiUrl(req?.baseURL || baseURL || "");
};

// Spinner
axios.interceptors.request.use(
  (req) => {
    if (!req.skipLoader) {
      store.dispatch({ type: OPEN_LOADER });
    }

    // Session cookies + the header that lets the API read them. The browser
    // never sends a bearer token any more (an old "Bearer null" would also
    // make the API ignore the cookie).
    if (isApiRequest(req)) {
      req.withCredentials = true;
      setHeader(req, CLIENT_HEADER, clientKind());
      removeHeader(req, "Authorization");
    }
    req._sentAt = Date.now();

    return req;
  },
  (error) => {
    // A request that fails before it is sent still opened the loader above, so
    // it must be closed here or the modal spinner stays on screen forever.
    // Rejecting (rather than returning undefined) also stops callers receiving
    // an undefined response and throwing on `res.data`.
    store.dispatch({ type: CLOSE_LOADER });
    console.log(error);
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (res) => {
    if (!res?.config?.skipLoader) {
      store.dispatch({ type: CLOSE_LOADER });
    }
    return res;
  },
  (err) => {
    if (err.message === "Network Error") {
      // Handle network errors here
    }
    // Session cookie missing / expired / rejected: refresh once (shared by
    // every request failing at the same time), then retry this request once.
    const config = err?.config;
    const sessionError =
      config &&
      !config.skipAuthRefresh &&
      isApiRequest(config) &&
      isSessionError(err?.response?.status, err?.response?.data?.code);
    if (sessionError && !config._authRetry && canRefresh()) {
      config._authRetry = true;
      return ensureFreshSession(config._sentAt)
        .then(() => axios(config))
        .catch((refreshError) => {
          if (!config.skipLoader) store.dispatch({ type: CLOSE_LOADER });
          return Promise.reject(refreshError?.response ? refreshError : err);
        });
    }
    // Still rejected after a successful refresh: the session is over.
    if (sessionError && config._authRetry && isSignedIn()) forceLogin();
    // A sub-admin tried to edit or delete where they may only view.
    const denied = err?.response?.data;
    if (
      err?.response?.status === 403 &&
      denied?.code === "INSUFFICIENT_PERMISSIONS" &&
      denied?.requiredAction &&
      denied.requiredAction !== "view"
    ) {
      notifyPermissionDenied(denied.message);
    }
    if (!err?.config?.skipLoader) {
      store.dispatch({ type: CLOSE_LOADER });
    }
    return Promise.reject(err);
  }
);

// Use createRoot for React 19
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  // <React.StrictMode>
  <BrowserRouter>
    <Provider store={store}>
      <div className="new_theme">
        <App />
      </div>
    </Provider>
  </BrowserRouter>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
